import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert,
  Stack, IconButton, Card, CardContent, Grid2 as Grid, Divider,
  Avatar,
} from '@mui/material';
import { Refresh, Receipt, CheckCircle, Schedule } from '@mui/icons-material';
import api from '../../services/api';
import { nowCv } from '../../utils/date';

interface Fatura {
  ID: string;
  NUMERO: string;
  PERIODO_MES: number;
  PERIODO_ANO: number;
  TOTAL_TRANSACOES: number;
  TOTAL_VOLUME: number;
  TOTAL_COMISSAO: number;
  TOTAL_IVA: number;
  TOTAL_PAGAR: number;
  ESTADO: string;
  DT_EMISSAO: string;
  DT_VENCIMENTO: string;
  DT_PAGAMENTO: string | null;
}

const ESTADO_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  RASCUNHO: 'default',
  EMITIDA: 'warning',
  PAGA: 'success',
  CANCELADA: 'error',
};

const ESTADO_LABEL: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  EMITIDA: 'Emitida',
  PAGA: 'Paga',
  CANCELADA: 'Cancelada',
};

const MESES_FULL: string[] = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

const fmtDate = (dt: string | null) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', year: 'numeric' });
};

// DT_VENCIMENTO é DATE (00:00Z) → mostrar em UTC.
const fmtDia = (dt: string | null) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function AgenteComissoes() {
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFaturas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/agente/comissoes');
      setFaturas(res.data?.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar comissões');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFaturas(); }, [fetchFaturas]);

  // Summary totals
  const totalEmitida = faturas
    .filter(f => f.ESTADO === 'EMITIDA')
    .reduce((sum, f) => sum + f.TOTAL_PAGAR, 0);

  const totalPaga = faturas
    .filter(f => f.ESTADO === 'PAGA')
    .reduce((sum, f) => sum + f.TOTAL_PAGAR, 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
      {/* Page header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Minhas Comissões
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Extracto mensal de comissões por serviços processados
          </Typography>
        </Box>
        <IconButton onClick={fetchFaturas} disabled={loading} color="primary">
          <Refresh />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary banner */}
      {faturas.length > 0 && (
        <Grid container spacing={2} mb={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                borderColor: 'warning.200',
                bgcolor: 'warning.50',
                height: '100%',
              }}
            >
              <CardContent sx={{ p: '16px !important' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: 'warning.100',
                      display: 'flex',
                      flexShrink: 0,
                    }}
                  >
                    <Schedule color="warning" sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5 }}>
                      A RECEBER
                    </Typography>
                    <Typography variant="h5" fontWeight={800} color="warning.main" lineHeight={1.1}>
                      {fmt(totalEmitida)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Facturas emitidas por pagar
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                borderColor: 'success.200',
                bgcolor: 'success.50',
                height: '100%',
              }}
            >
              <CardContent sx={{ p: '16px !important' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: 'success.100',
                      display: 'flex',
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircle color="success" sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5 }}>
                      JÁ RECEBIDO
                    </Typography>
                    <Typography variant="h5" fontWeight={800} color="success.main" lineHeight={1.1}>
                      {fmt(totalPaga)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Facturas já liquidadas
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : faturas.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Receipt sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              Sem facturas de comissão
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 380, mx: 'auto', mt: 0.5 }}>
              As facturas de comissão são geradas mensalmente pelo administrador com base nas
              transacções processadas no mês anterior.
            </Typography>
          </Box>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {faturas.map(f => {
            const isPaid = f.ESTADO === 'PAGA';
            const isOverdue =
              f.ESTADO === 'EMITIDA' &&
              f.DT_VENCIMENTO &&
              new Date(f.DT_VENCIMENTO) < nowCv();

            return (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={f.ID}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    height: '100%',
                    borderColor: isPaid
                      ? 'success.200'
                      : isOverdue
                      ? 'error.200'
                      : f.ESTADO === 'EMITIDA'
                      ? 'warning.200'
                      : 'divider',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.1)' },
                  }}
                >
                  <CardContent sx={{ p: '16px !important' }}>
                    {/* Card header: month + status */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Box>
                        <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                          {MESES_FULL[f.PERIODO_MES - 1]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {f.PERIODO_ANO}
                        </Typography>
                      </Box>
                      <Chip
                        label={ESTADO_LABEL[f.ESTADO] || f.ESTADO}
                        size="small"
                        color={ESTADO_COLOR[f.ESTADO] || 'default'}
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    {/* Invoice number */}
                    <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: isPaid ? 'success.100' : 'action.hover',
                          flexShrink: 0,
                        }}
                      >
                        <Receipt sx={{ fontSize: 14, color: isPaid ? 'success.main' : 'text.secondary' }} />
                      </Avatar>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                        {f.NUMERO}
                      </Typography>
                    </Stack>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Stats */}
                    <Stack spacing={0.75} mb={1.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Transacções</Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {f.TOTAL_TRANSACOES}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Volume</Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {fmt(f.TOTAL_VOLUME)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Comissão</Typography>
                        <Typography variant="caption" fontWeight={600} color="success.main">
                          {fmt(f.TOTAL_COMISSAO)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">IVA (15%)</Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {fmt(f.TOTAL_IVA)}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Total */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        Total a pagar
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        color={isPaid ? 'success.main' : 'text.primary'}
                      >
                        {fmt(f.TOTAL_PAGAR)}
                      </Typography>
                    </Stack>

                    {/* Dates */}
                    <Stack spacing={0.5}>
                      {f.DT_VENCIMENTO && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color={isOverdue ? 'error.main' : 'text.disabled'}>
                            {isOverdue ? 'Vencida em' : 'Vencimento'}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={isOverdue ? 700 : 400}
                            color={isOverdue ? 'error.main' : 'text.secondary'}
                          >
                            {fmtDia(f.DT_VENCIMENTO)}
                          </Typography>
                        </Stack>
                      )}
                      {f.DT_PAGAMENTO && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.disabled">Pago em</Typography>
                          <Typography variant="caption" color="success.main" fontWeight={600}>
                            {fmtDate(f.DT_PAGAMENTO)}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
